import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Alert
} from '@mui/material';
import { useTheme } from '@emotion/react';
import { tokens } from '../theme';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PolylineIcon from '@mui/icons-material/Polyline';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

/**
 * InteractiveImageMapper Component
 * Kullanıcının görsel üzerinde mouse ile alan çizmesini sağlar
 */
const InteractiveImageMapper = ({ 
  imageUrl = '', 
  areas = [], 
  onAreasChange = () => {},
  disabled = false 
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [currentRect, setCurrentRect] = useState(null);
  const [drawMode, setDrawMode] = useState(false);
  const [shapeType, setShapeType] = useState('rectangle'); // 'rectangle', 'circle', or 'polygon'
  
  // Polygon çizimi için state'ler
  const [polygonPoints, setPolygonPoints] = useState([]);
  const [isDrawingPolygon, setIsDrawingPolygon] = useState(false);
  const [currentMousePos, setCurrentMousePos] = useState(null);
  
  // Etiket görünürlük kontrolü
  const [showAllLabels, setShowAllLabels] = useState(false);
  const [hoveredAreaId, setHoveredAreaId] = useState(null);
  
  // Dialog states
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [tempArea, setTempArea] = useState(null);
  const [editingArea, setEditingArea] = useState(null);
  const [areaName, setAreaName] = useState('');
  const [areaValue, setAreaValue] = useState('');
  const [areaDescription, setAreaDescription] = useState('');
  const [areaColor, setAreaColor] = useState('#4cceac'); // Varsayılan yeşil
  
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  // Görsel yüklendiğinde boyutları al
  useEffect(() => {
    if (imageUrl && containerRef.current) {
      const img = new Image();
      img.onload = () => {
        setImageSize({ width: img.width, height: img.height });
        
        const container = containerRef.current;
        const containerWidth = container.offsetWidth;
        const aspectRatio = img.height / img.width;
        const containerHeight = containerWidth * aspectRatio;
        
        setContainerSize({ width: containerWidth, height: containerHeight });
      };
      img.src = imageUrl;
    }
  }, [imageUrl]);

  // Canvas'ı çiz
  useEffect(() => {
    if (!canvasRef.current || !imageUrl) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Canvas'ı temizle
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Kaydedilmiş alanları çiz
    areas.forEach((area) => {
      drawArea(ctx, area, false);
    });
    
    // Çizilen geçici alanı çiz
    if (currentRect && isDrawing) {
      drawArea(ctx, currentRect, true);
    }
    
    // Polygon çizimi devam ediyorsa
    if (isDrawingPolygon && polygonPoints.length > 0) {
      drawPolygon(ctx, polygonPoints, currentMousePos, true);
    }
  }, [areas, currentRect, isDrawing, imageUrl, polygonPoints, isDrawingPolygon, currentMousePos, showAllLabels, hoveredAreaId]);

  const drawArea = (ctx, area, isTemp = false) => {
    // Alan rengini kullan, yoksa varsayılan
    const areaColor = area.color || '#4cceac';
    const rgb = hexToRgb(areaColor);
    
    ctx.strokeStyle = isTemp ? colors.greenAccent[400] : areaColor;
    ctx.fillStyle = isTemp 
      ? `rgba(76, 206, 172, 0.2)` 
      : `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.3)`;
    ctx.lineWidth = 2;

    let centerX, centerY;

    if (area.shape === 'polygon') {
      // Polygon çiz
      if (area.points && area.points.length > 0) {
        ctx.beginPath();
        ctx.moveTo(area.points[0].x, area.points[0].y);
        for (let i = 1; i < area.points.length; i++) {
          ctx.lineTo(area.points[i].x, area.points[i].y);
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        
        // Polygon merkezini hesapla
        let sumX = 0, sumY = 0;
        area.points.forEach(p => {
          sumX += p.x;
          sumY += p.y;
        });
        centerX = sumX / area.points.length;
        centerY = sumY / area.points.length;
      }
    } else {
      centerX = area.x + (area.width || 0) / 2;
      centerY = area.y + (area.height || 0) / 2;

      if (area.shape === 'circle' || shapeType === 'circle') {
        const radius = Math.sqrt(
          Math.pow(area.width || 0, 2) + Math.pow(area.height || 0, 2)
        ) / 2;
        
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();
      } else {
        // Rectangle
        ctx.fillRect(area.x, area.y, area.width || 0, area.height || 0);
        ctx.strokeRect(area.x, area.y, area.width || 0, area.height || 0);
      }
    }

    // Alan adını merkeze yaz (sadece kaydedilmiş alanlar için)
    // Görünürlük kontrolü: showAllLabels açıksa VEYA hover ediliyorsa göster
    const shouldShowLabel = area.name && !isTemp && centerX && centerY && 
                           (showAllLabels || hoveredAreaId === area.id);
    
    if (shouldShowLabel) {
      const label = area.name.substring(0, 15);
      
      // Text boyutunu ölç - okunabilir font
      ctx.font = 'bold 12px Arial';
      const textMetrics = ctx.measureText(label);
      const textWidth = textMetrics.width;
      const textHeight = 14;
      
      // Arka plan kutusu - siyah, biraz daha koyu
      const padding = 4;
      ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
      ctx.beginPath();
      ctx.roundRect(
        centerX - textWidth / 2 - padding,
        centerY - textHeight / 2 - padding,
        textWidth + padding * 2,
        textHeight + padding * 2,
        4
      );
      ctx.fill();
      
      // Text - beyaz, kalın, tam merkez
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, centerX, centerY);
    }
  };

  // Polygon çizim fonksiyonu
  const drawPolygon = (ctx, points, currentPos, isTemp = false) => {
    if (points.length === 0) return;
    
    ctx.strokeStyle = isTemp ? colors.greenAccent[400] : colors.blueAccent[400];
    ctx.fillStyle = isTemp 
      ? `rgba(76, 206, 172, 0.2)` 
      : `rgba(100, 181, 246, 0.2)`;
    ctx.lineWidth = 2;
    
    // Çizgileri çiz
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    
    // Eğer mouse pozisyonu varsa, son noktadan mouse'a çizgi çiz
    if (currentPos && isTemp) {
      ctx.lineTo(currentPos.x, currentPos.y);
    }
    
    // Eğer 3'ten fazla nokta varsa ve geçici değilse, poligonu kapat
    if (points.length > 2 && !isTemp) {
      ctx.closePath();
      ctx.fill();
    }
    
    ctx.stroke();
    
    // Noktaları işaretle
    points.forEach((point, index) => {
      ctx.fillStyle = colors.greenAccent[500];
      ctx.beginPath();
      ctx.arc(point.x, point.y, 5, 0, 2 * Math.PI);
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1;
      ctx.stroke();
      
      // İlk noktayı özel işaretle (kapatma için)
      if (index === 0 && points.length > 2) {
        ctx.beginPath();
        ctx.arc(point.x, point.y, 8, 0, 2 * Math.PI);
        ctx.strokeStyle = colors.greenAccent[300];
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    });
  };

  // Hex rengi RGB'ye çevir
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 76, g: 206, b: 172 }; // Varsayılan yeşil
  };

  const getMousePos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const handleMouseDown = (e) => {
    if (!drawMode || disabled) return;
    
    const pos = getMousePos(e);
    
    // Polygon modundaysa
    if (shapeType === 'polygon') {
      // İlk nokta ise, polygon çizimini başlat
      if (!isDrawingPolygon) {
        setIsDrawingPolygon(true);
        setPolygonPoints([pos]);
      } else {
        // İlk noktaya yakınsa, poligonu kapat
        const firstPoint = polygonPoints[0];
        const distance = Math.sqrt(
          Math.pow(pos.x - firstPoint.x, 2) + Math.pow(pos.y - firstPoint.y, 2)
        );
        
        if (distance < 10 && polygonPoints.length > 2) {
          // Polygon tamamlandı, kaydetme dialogunu aç
          finishPolygon();
        } else {
          // Yeni nokta ekle
          setPolygonPoints([...polygonPoints, pos]);
        }
      }
    } else {
      // Rectangle veya circle modu
      setIsDrawing(true);
      setStartPos(pos);
      setCurrentRect({
        x: pos.x,
        y: pos.y,
        width: 0,
        height: 0,
        shape: shapeType
      });
    }
  };

  const handleMouseMove = (e) => {
    const pos = getMousePos(e);
    
    // Polygon çizimi devam ediyorsa, mouse pozisyonunu güncelle
    if (isDrawingPolygon && shapeType === 'polygon') {
      setCurrentMousePos(pos);
      return;
    }
    
    // Hover edilen alanı bul (sadece çizim modunda değilse)
    if (!isDrawing && !drawMode) {
      const hoveredArea = areas.find(area => {
        if (area.shape === 'polygon' && area.points) {
          // Polygon için point-in-polygon kontrolü
          return isPointInPolygon(pos, area.points);
        } else if (area.shape === 'circle') {
          const centerX = area.x + area.width / 2;
          const centerY = area.y + area.height / 2;
          const radius = Math.sqrt(Math.pow(area.width, 2) + Math.pow(area.height, 2)) / 2;
          const distance = Math.sqrt(Math.pow(pos.x - centerX, 2) + Math.pow(pos.y - centerY, 2));
          return distance <= radius;
        } else {
          // Rectangle
          return pos.x >= area.x && pos.x <= area.x + area.width &&
                 pos.y >= area.y && pos.y <= area.y + area.height;
        }
      });
      
      setHoveredAreaId(hoveredArea ? hoveredArea.id : null);
    }
    
    if (!isDrawing || !drawMode) return;
    
    setCurrentRect({
      x: Math.min(startPos.x, pos.x),
      y: Math.min(startPos.y, pos.y),
      width: Math.abs(pos.x - startPos.x),
      height: Math.abs(pos.y - startPos.y),
      shape: shapeType
    });
  };
  
  // Point-in-polygon algoritması (ray casting)
  const isPointInPolygon = (point, polygon) => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x, yi = polygon[i].y;
      const xj = polygon[j].x, yj = polygon[j].y;
      
      const intersect = ((yi > point.y) !== (yj > point.y))
          && (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  };

  const handleMouseUp = (e) => {
    if (!isDrawing || !drawMode) return;
    
    setIsDrawing(false);
    
    // Çok küçük alanları yoksay
    if (currentRect.width < 10 || currentRect.height < 10) {
      setCurrentRect(null);
      return;
    }
    
    // Alan bilgilerini almak için dialog aç
    setTempArea(currentRect);
    setSaveDialogOpen(true);
  };

  const handleSaveArea = () => {
    if (!areaName.trim() || !tempArea) return;
    
    let newArea;
    
    if (tempArea.shape === 'polygon') {
      // Polygon için kaydetme
      const normalizedPoints = tempArea.points.map(p => ({
        x: (p.x / containerSize.width) * 100,
        y: (p.y / containerSize.height) * 100
      }));
      
      // SVG path oluştur
      let path = `M ${normalizedPoints[0].x} ${normalizedPoints[0].y}`;
      for (let i = 1; i < normalizedPoints.length; i++) {
        path += ` L ${normalizedPoints[i].x} ${normalizedPoints[i].y}`;
      }
      path += ' Z';
      
      // Merkez noktayı hesapla
      let sumX = 0, sumY = 0;
      normalizedPoints.forEach(p => {
        sumX += p.x;
        sumY += p.y;
      });
      const centerX = sumX / normalizedPoints.length;
      const centerY = sumY / normalizedPoints.length;
      
      newArea = {
        id: Date.now().toString(),
        name: areaName,
        value: areaValue || areaName,
        description: areaDescription,
        shape: 'polygon',
        points: tempArea.points, // Canvas koordinatları
        path: path,
        labelX: centerX,
        labelY: centerY,
        label: areaName.substring(0, 15),
        color: areaColor // Renk eklendi
      };
    } else {
      // Rectangle veya Circle için kaydetme
      const normalizedArea = {
        ...tempArea,
        x: (tempArea.x / containerSize.width) * 100,
        y: (tempArea.y / containerSize.height) * 100,
        width: (tempArea.width / containerSize.width) * 100,
        height: (tempArea.height / containerSize.height) * 100,
      };
      
      let path;
      if (tempArea.shape === 'circle') {
        const cx = normalizedArea.x + normalizedArea.width / 2;
        const cy = normalizedArea.y + normalizedArea.height / 2;
        const r = Math.sqrt(
          Math.pow(normalizedArea.width, 2) + Math.pow(normalizedArea.height, 2)
        ) / 2;
        path = `M ${cx - r} ${cy} A ${r} ${r} 0 1 1 ${cx + r} ${cy} A ${r} ${r} 0 1 1 ${cx - r} ${cy}`;
      } else {
        const { x, y, width, height } = normalizedArea;
        path = `M ${x} ${y} L ${x + width} ${y} L ${x + width} ${y + height} L ${x} ${y + height} Z`;
      }
      
      newArea = {
        id: Date.now().toString(),
        name: areaName,
        value: areaValue || areaName,
        description: areaDescription,
        shape: tempArea.shape,
        x: tempArea.x,
        y: tempArea.y,
        width: tempArea.width,
        height: tempArea.height,
        path: path,
        labelX: normalizedArea.x + normalizedArea.width / 2,
        labelY: normalizedArea.y + normalizedArea.height / 2,
        label: areaName.substring(0, 15),
        color: areaColor // Renk eklendi
      };
    }
    
    onAreasChange([...areas, newArea]);
    
    // Reset
    setCurrentRect(null);
    setTempArea(null);
    setAreaName('');
    setAreaValue('');
    setAreaDescription('');
    setAreaColor('#4cceac'); // Rengi sıfırla
    setSaveDialogOpen(false);
  };

  const handleCancelSave = () => {
    setCurrentRect(null);
    setTempArea(null);
    setAreaName('');
    setAreaValue('');
    setAreaDescription('');
    setAreaColor('#4cceac'); // Rengi sıfırla
    setSaveDialogOpen(false);
    
    // Polygon çizimini de iptal et
    setPolygonPoints([]);
    setIsDrawingPolygon(false);
    setCurrentMousePos(null);
  };
  
  const finishPolygon = () => {
    if (polygonPoints.length < 3) return;
    
    setTempArea({
      shape: 'polygon',
      points: polygonPoints
    });
    setSaveDialogOpen(true);
  };
  
  const cancelPolygon = () => {
    setPolygonPoints([]);
    setIsDrawingPolygon(false);
    setCurrentMousePos(null);
  };

  const handleDeleteArea = (areaId) => {
    onAreasChange(areas.filter(a => a.id !== areaId));
  };

  // Alan düzenleme fonksiyonları
  const handleEditArea = (area) => {
    setEditingArea(area);
    setAreaName(area.name);
    setAreaValue(area.value.toString());
    setAreaDescription(area.description || '');
    setAreaColor(area.color || '#4cceac');
    setEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    if (!areaName.trim() || !areaValue.trim()) {
      return;
    }

    const updatedAreas = areas.map(area => 
      area.id === editingArea.id 
        ? {
            ...area,
            name: areaName.trim(),
            value: parseFloat(areaValue) || 0,
            description: areaDescription.trim(),
            color: areaColor
          }
        : area
    );

    onAreasChange(updatedAreas);
    handleCancelEdit();
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingArea(null);
    setAreaName('');
    setAreaValue('');
    setAreaDescription('');
    setAreaColor('#4cceac');
  };

  const toggleDrawMode = () => {
    setDrawMode(!drawMode);
    setCurrentRect(null);
    setIsDrawing(false);
    
    // Polygon çizimini de iptal et
    if (isDrawingPolygon) {
      cancelPolygon();
    }
  };

  if (!imageUrl) {
    return (
      <Paper
        sx={{
          p: 4,
          textAlign: 'center',
          backgroundColor: colors.primary[600],
          border: `2px dashed ${colors.grey[600]}`,
          borderRadius: 2
        }}
      >
        <Typography variant="body1" sx={{ color: colors.grey[400] }}>
          Önce bir görsel yükleyin
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Kontrol Paneli */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: colors.primary[500],
          border: `2px solid ${colors.blueAccent[700]}`,
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant={drawMode ? "contained" : "outlined"}
              onClick={toggleDrawMode}
              disabled={disabled}
              startIcon={drawMode ? <CancelIcon /> : <EditIcon />}
              sx={{
                backgroundColor: drawMode ? colors.greenAccent[600] : 'transparent',
                borderColor: colors.greenAccent[500],
                color: drawMode ? 'white' : colors.greenAccent[400],
                '&:hover': {
                  backgroundColor: drawMode ? colors.greenAccent[700] : colors.greenAccent[900]
                }
              }}
            >
              {drawMode ? 'Çizim Modunu Kapat' : 'Alan Çiz'}
            </Button>

            {drawMode && (
              <ToggleButtonGroup
                value={shapeType}
                exclusive
                onChange={(e, newShape) => {
                  if (newShape) {
                    // Polygon çizimi devam ediyorsa iptal et
                    if (isDrawingPolygon) {
                      cancelPolygon();
                    }
                    setShapeType(newShape);
                  }
                }}
                size="small"
              >
                <ToggleButton value="rectangle" sx={{ px: 2 }}>
                  <CropSquareIcon sx={{ mr: 1 }} />
                  Dikdörtgen
                </ToggleButton>
                <ToggleButton value="circle" sx={{ px: 2 }}>
                  <RadioButtonUncheckedIcon sx={{ mr: 1 }} />
                  Daire
                </ToggleButton>
                <ToggleButton value="polygon" sx={{ px: 2 }}>
                  <PolylineIcon sx={{ mr: 1 }} />
                  Poligon
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            
            {drawMode && isDrawingPolygon && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={finishPolygon}
                  disabled={polygonPoints.length < 3}
                  sx={{
                    backgroundColor: colors.greenAccent[600],
                    '&:hover': { backgroundColor: colors.greenAccent[700] }
                  }}
                >
                  Poligonu Tamamla ({polygonPoints.length} nokta)
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={cancelPolygon}
                  sx={{
                    borderColor: colors.redAccent[500],
                    color: colors.redAccent[400],
                    '&:hover': { 
                      borderColor: colors.redAccent[400],
                      backgroundColor: colors.redAccent[900]
                    }
                  }}
                >
                  İptal
                </Button>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {areas.length > 0 && (
              <Button
                size="small"
                variant={showAllLabels ? "contained" : "outlined"}
                onClick={() => setShowAllLabels(!showAllLabels)}
                startIcon={showAllLabels ? <VisibilityOffIcon /> : <VisibilityIcon />}
                sx={{
                  borderColor: colors.blueAccent[500],
                  color: showAllLabels ? 'white' : colors.blueAccent[400],
                  backgroundColor: showAllLabels ? colors.blueAccent[600] : 'transparent',
                  '&:hover': {
                    backgroundColor: showAllLabels ? colors.blueAccent[700] : colors.blueAccent[900]
                  }
                }}
              >
                {showAllLabels ? 'Etiketleri Gizle' : 'Etiketleri Göster'}
              </Button>
            )}
            
            <Chip
              label={`${areas.length} alan tanımlandı`}
              sx={{
                backgroundColor: colors.greenAccent[700],
                color: colors.grey[100],
                fontWeight: 'bold'
              }}
            />
          </Box>
        </Box>

        <Alert severity={drawMode ? "info" : "success"} sx={{ mt: 2 }}>
          {drawMode ? (
            shapeType === 'polygon' ? (
              <>
                🖱️ Poligon çizmek için noktaları tıklayın. İlk noktaya tıklayarak veya "Poligonu Tamamla" butonuna basarak bitirin.
                {isDrawingPolygon && ` (${polygonPoints.length} nokta)`}
              </>
            ) : (
              <>
                🖱️ Mouse ile {shapeType === 'rectangle' ? 'dikdörtgen' : 'daire'} çizmek için tıklayıp sürükleyin. 
                Çizim tamamlandığında alan bilgilerini girmeniz istenecek.
              </>
            )
          ) : (
            showAllLabels ? (
              '💡 Tüm etiketler görünür - Etiketleri gizlemek için butona tıklayın'
            ) : (
              '💡 Etiketleri görmek için alana mouse ile gelin veya "Etiketleri Göster" butonuna tıklayın'
            )
          )}
        </Alert>
      </Paper>

      {/* Canvas Alanı */}
      <Paper
        ref={containerRef}
        sx={{
          position: 'relative',
          backgroundColor: colors.primary[600],
          border: `3px solid ${drawMode ? colors.greenAccent[500] : colors.blueAccent[700]}`,
          borderRadius: 2,
          overflow: 'hidden',
          cursor: drawMode ? 'crosshair' : 'default',
          transition: 'border-color 0.3s ease'
        }}
      >
        {/* Arka plan görseli */}
        <img
          src={imageUrl}
          alt="Mapping"
          style={{
            width: '100%',
            height: 'auto',
            display: 'block',
            opacity: 0.9
          }}
          onLoad={(e) => {
            const img = e.target;
            setContainerSize({ 
              width: img.offsetWidth, 
              height: img.offsetHeight 
            });
          }}
        />

        {/* Canvas overlay */}
        <canvas
          ref={canvasRef}
          width={containerSize.width}
          height={containerSize.height}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={() => {
            if (isDrawing) {
              setIsDrawing(false);
              setCurrentRect(null);
            }
            setHoveredAreaId(null);
          }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'auto', // Her zaman hover için aktif
            cursor: drawMode ? 'crosshair' : (hoveredAreaId ? 'pointer' : 'default')
          }}
        />
      </Paper>

      {/* Tanımlı Alanlar Listesi */}
      {areas.length > 0 && (
        <Paper
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: colors.primary[500],
            border: `2px solid ${colors.blueAccent[700]}`,
            borderRadius: 2
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, color: colors.greenAccent[400] }}>
            📋 Tanımlı Alanlar
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {areas.map((area) => (
              <Box
                key={area.id}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 1.5,
                  backgroundColor: colors.primary[600],
                  borderRadius: 1,
                  border: `1px solid ${colors.blueAccent[800]}`
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {/* Renk göstergesi */}
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: area.color || colors.greenAccent[500],
                      borderRadius: 1,
                      border: `2px solid ${colors.grey[700]}`
                    }}
                  />
                  
                  {area.shape === 'polygon' ? (
                    <PolylineIcon sx={{ color: area.color || colors.blueAccent[400] }} />
                  ) : area.shape === 'circle' ? (
                    <RadioButtonUncheckedIcon sx={{ color: area.color || colors.blueAccent[400] }} />
                  ) : (
                    <CropSquareIcon sx={{ color: area.color || colors.blueAccent[400] }} />
                  )}
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {area.name}
                    </Typography>
                    {area.description && (
                      <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                        {area.description}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={area.value}
                    size="small"
                    sx={{
                      backgroundColor: area.color || colors.greenAccent[700],
                      color: colors.grey[100]
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    onClick={() => handleEditArea(area)}
                    disabled={disabled}
                    sx={{ color: colors.blueAccent[400] }}
                    title="Düzenle"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDeleteArea(area.id)}
                    disabled={disabled}
                    sx={{ color: colors.redAccent[400] }}
                    title="Sil"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Alan Kaydetme Dialog */}
      <Dialog
        open={saveDialogOpen}
        onClose={handleCancelSave}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.primary[400],
            borderRadius: 3
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: `2px solid ${colors.greenAccent[400]}` }}>
          <Typography variant="h5" fontWeight="bold">
            🗺️ Alan Bilgilerini Girin
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            fullWidth
            label="Alan Adı *"
            variant="outlined"
            value={areaName}
            onChange={(e) => setAreaName(e.target.value)}
            sx={{ mb: 2 }}
            placeholder="Örn: Medial Femoral Condyle"
            autoFocus
          />
          <TextField
            fullWidth
            label="Alan Değeri (skorlama için - sadece sayı)"
            variant="outlined"
            type="number"
            value={areaValue}
            onChange={(e) => {
              const value = e.target.value;
              // Sadece sayısal değerlere izin ver
              if (value === '' || !isNaN(value)) {
                setAreaValue(value);
              }
            }}
            sx={{ mb: 2 }}
            placeholder="Örn: 1, 2, 3, 4"
            inputProps={{
              min: 0,
              step: 1
            }}
          />
          <TextField
            fullWidth
            label="Açıklama (opsiyonel)"
            variant="outlined"
            multiline
            rows={3}
            value={areaDescription}
            onChange={(e) => setAreaDescription(e.target.value)}
            placeholder="Bu alan hakkında kısa açıklama"
            sx={{ mb: 2 }}
          />
          
          {/* Renk Seçici */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: colors.grey[100] }}>
              Alan Rengi
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {[
                { name: 'Yeşil', color: '#4cceac' },
                { name: 'Mavi', color: '#6870fa' },
                { name: 'Kırmızı', color: '#f44336' },
                { name: 'Turuncu', color: '#ff9800' },
                { name: 'Mor', color: '#9c27b0' },
                { name: 'Sarı', color: '#ffc107' },
                { name: 'Pembe', color: '#e91e63' },
                { name: 'Cyan', color: '#00bcd4' },
              ].map((preset) => (
                <Box
                  key={preset.color}
                  onClick={() => setAreaColor(preset.color)}
                  sx={{
                    width: 50,
                    height: 50,
                    backgroundColor: preset.color,
                    borderRadius: 2,
                    cursor: 'pointer',
                    border: areaColor === preset.color 
                      ? `4px solid ${colors.grey[100]}` 
                      : '2px solid transparent',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                    }
                  }}
                >
                  {areaColor === preset.color && (
                    <Typography sx={{ color: 'white', fontSize: '20px' }}>✓</Typography>
                  )}
                </Box>
              ))}
              
              {/* Custom renk seçici */}
              <Box sx={{ position: 'relative' }}>
                <input
                  type="color"
                  value={areaColor}
                  onChange={(e) => setAreaColor(e.target.value)}
                  style={{
                    width: '50px',
                    height: '50px',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    outline: areaColor.length === 7 && 
                            !['#4cceac', '#6870fa', '#f44336', '#ff9800', '#9c27b0', '#ffc107', '#e91e63', '#00bcd4'].includes(areaColor)
                      ? `4px solid ${colors.grey[100]}` 
                      : 'none'
                  }}
                  title="Özel renk seç"
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleCancelSave}
            variant="outlined"
            sx={{
              borderColor: colors.redAccent[500],
              color: colors.redAccent[400]
            }}
          >
            İptal
          </Button>
          <Button
            onClick={handleSaveArea}
            variant="contained"
            disabled={!areaName.trim()}
            startIcon={<SaveIcon />}
            sx={{
              backgroundColor: colors.greenAccent[600],
              '&:hover': {
                backgroundColor: colors.greenAccent[700]
              }
            }}
          >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alan Düzenleme Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCancelEdit}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.primary[400],
            borderRadius: 3
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: `2px solid ${colors.blueAccent[400]}` }}>
          <Typography variant="h5" fontWeight="bold">
            ✏️ Alan Düzenle
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Alan Adı"
              value={areaName}
              onChange={(e) => setAreaName(e.target.value)}
              fullWidth
              variant="outlined"
              placeholder="Örn: MFC, LFC, Patella"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.primary[500]
                }
              }}
            />
            
            <TextField
              label="Alan Değeri (Sayısal)"
              type="number"
              value={areaValue}
              onChange={(e) => setAreaValue(e.target.value)}
              fullWidth
              variant="outlined"
              placeholder="Örn: 5"
              inputProps={{ min: 0, step: 1 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.primary[500]
                }
              }}
            />
            
            <TextField
              label="Açıklama (Opsiyonel)"
              value={areaDescription}
              onChange={(e) => setAreaDescription(e.target.value)}
              fullWidth
              variant="outlined"
              multiline
              rows={2}
              placeholder="Alan hakkında ek bilgi..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.primary[500]
                }
              }}
            />
            
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: colors.grey[100] }}>
                Alan Rengi
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {[
                  { name: 'Yeşil', color: '#4cceac' },
                  { name: 'Mavi', color: '#6870fa' },
                  { name: 'Kırmızı', color: '#f44336' },
                  { name: 'Turuncu', color: '#ff9800' },
                  { name: 'Mor', color: '#9c27b0' },
                  { name: 'Sarı', color: '#ffc107' },
                  { name: 'Pembe', color: '#e91e63' },
                  { name: 'Cyan', color: '#00bcd4' },
                ].map((preset) => (
                  <Box
                    key={preset.color}
                    onClick={() => setAreaColor(preset.color)}
                    sx={{
                      width: 50,
                      height: 50,
                      backgroundColor: preset.color,
                      borderRadius: 2,
                      cursor: 'pointer',
                      border: areaColor === preset.color 
                        ? `4px solid ${colors.grey[100]}` 
                        : '2px solid transparent',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                      }
                    }}
                  >
                    {areaColor === preset.color && (
                      <Typography sx={{ color: 'white', fontSize: '20px' }}>✓</Typography>
                    )}
                  </Box>
                ))}
                
                {/* Custom renk seçici */}
                <Box sx={{ position: 'relative' }}>
                  <input
                    type="color"
                    value={areaColor}
                    onChange={(e) => setAreaColor(e.target.value)}
                    style={{
                      width: '50px',
                      height: '50px',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      outline: areaColor.length === 7 && 
                              !['#4cceac', '#6870fa', '#f44336', '#ff9800', '#9c27b0', '#ffc107', '#e91e63', '#00bcd4'].includes(areaColor)
                        ? `4px solid ${colors.grey[100]}` 
                        : 'none'
                    }}
                    title="Özel renk seç"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleCancelEdit}
            variant="outlined"
            sx={{
              borderColor: colors.redAccent[500],
              color: colors.redAccent[400]
            }}
          >
            İptal
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={!areaName.trim() || !areaValue.trim()}
            startIcon={<SaveIcon />}
            sx={{
              backgroundColor: colors.blueAccent[600],
              '&:hover': {
                backgroundColor: colors.blueAccent[700]
              }
            }}
          >
            Güncelle
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InteractiveImageMapper;

